/** The seven command intents as a closed union, their identity and their progression (ruling 0.7, UI spec §4). */
import { REASON_MAX } from "../contracts/account.ts";
import { COMMAND_STATES, type CommandOutcome, type CommandRequest } from "../contracts/commands.ts";
import type { CommandState } from "../contracts/commands.ts";
import type { Environment, Mode } from "../contracts/envelope.ts";
import { formatDurationUs, parseInstant } from "./format.ts";
import { secondsLeft, serverNowMicros, type Mono, type ServerClock } from "./time.ts";

export const COMMAND_INTENTS = [
  "pause-entries",
  "reduce-position",
  "flatten-account",
  "cancel-order",
  "cancel-all-orders",
  "manual-takeover",
  "resume",
] as const;

/**
 * The two intents the seven do not cover (INC-0807): changing a working order and raising a risk
 * limit. The seven stay the closed set of "the seven commands"; these exist only behind routing.
 */
export const ROUTED_EXTRA_INTENTS = ["modify-order", "increase-risk"] as const;

export type CommandIntent =
  (typeof COMMAND_INTENTS)[number] | (typeof ROUTED_EXTRA_INTENTS)[number];

/** A money amount an intent asks for, in rupees on screen and integer paise on the wire. */
export interface AmountSpec {
  readonly label: string;
  /** The payload key the paise go under. */
  readonly key: string;
}

export interface IntentSpec {
  readonly label: string;
  readonly action: string;
  /** Whether the server demands a fresh step-up and a single-use authorisation (stepup.needs_step_up). */
  readonly stepUp: boolean;
  /** What applying it does, shown before the confirm button (UI spec §4: effects). */
  readonly effects: readonly string[];
  /** Whether the dialog asks for a whole quantity. */
  readonly quantity: boolean;
  /** Whether the action is a P0-E one that exists only when broker routing is enabled (ruling 0.14). */
  readonly routed: boolean;
  /** The money amount the dialog asks for, or `null`. */
  readonly amount: AmountSpec | null;
}

/** The label, the server action, the step-up need and the effects of each intent: seven distinct commands. */
export const INTENTS: Readonly<Record<CommandIntent, IntentSpec>> = {
  "pause-entries": {
    label: "Pause new entries",
    action: "PAUSE_ENTRIES",
    stepUp: false,
    effects: [
      "Blocks new entries for this account at once.",
      "Open positions, working orders and protective stops are not touched: this does not close anything.",
    ],
    quantity: false,
    routed: false,
    amount: null,
  },
  "reduce-position": {
    label: "Reduce position",
    action: "REDUCE_POSITIONS",
    stepUp: false,
    effects: [
      "Asks the controller to exit the stated quantity of this one position through its safe exit sequence.",
      "The exit orders have their own broker lifecycle: a successful request is not a fill.",
    ],
    quantity: true,
    routed: true,
    amount: null,
  },
  "flatten-account": {
    label: "Flatten account",
    action: "FLATTEN",
    stepUp: false,
    effects: [
      "Asks the controller to exit every open position in this account and to cancel its entry orders, in its safe sequence.",
      "The exit orders have their own broker lifecycle: a successful request is not a fill.",
    ],
    quantity: false,
    routed: true,
    amount: null,
  },
  "cancel-order": {
    label: "Cancel order",
    action: "CANCEL_ORDER",
    stepUp: false,
    effects: [
      "Asks the broker to cancel this one order. The order stays working until the broker confirms.",
      "Cancelling a protective stop removes the protection of its position.",
    ],
    quantity: false,
    routed: true,
    amount: null,
  },
  "cancel-all-orders": {
    label: "Cancel all orders",
    action: "CANCEL_ALL",
    stepUp: false,
    effects: [
      "Asks the broker to cancel every working order in this account, protective stop orders included.",
      "Positions can be left without protection. This command needs its own authority, separate from cancelling one order.",
    ],
    quantity: false,
    routed: true,
    amount: null,
  },
  "manual-takeover": {
    label: "Take over manually",
    action: "TAKEOVER",
    stepUp: true,
    effects: [
      "Moves execution ownership for this account to you; the controller stops placing orders.",
      "Needs a fresh second-factor step-up. Logging out or closing the tab does not hand ownership back.",
    ],
    quantity: false,
    routed: true,
    amount: null,
  },
  resume: {
    label: "Resume entries",
    action: "RESUME_ENTRIES",
    stepUp: true,
    effects: [
      "Re-enables new entries after a pause. The server revalidates the state version and the risk limits.",
      "Needs a fresh second-factor step-up. Acknowledging an alert never does this.",
    ],
    quantity: false,
    routed: true,
    amount: null,
  },
  "modify-order": {
    label: "Modify order",
    action: "MODIFY_ORDER",
    stepUp: true,
    effects: [
      "Asks the broker to change the limit price of this one working order; the order keeps its identity.",
      "Each modification counts against the broker's per-order cap. The order stays as it was until the broker confirms.",
      "Needs a fresh second-factor step-up, because a modification can raise the order's exposure.",
    ],
    quantity: false,
    routed: true,
    amount: { label: "New limit price (rupees)", key: "new_price_paise" },
  },
  "increase-risk": {
    label: "Increase risk limit",
    action: "CHANGE_RISK",
    stepUp: true,
    effects: [
      "Raises one risk limit, so the account may take more risk under a new versioned policy.",
      "Needs a fresh second-factor step-up and is applied only after the server revalidates the state version. Lowering a limit is a different, immediate change.",
    ],
    quantity: false,
    routed: true,
    amount: { label: "New limit (rupees)", key: "new_limit_paise" },
  },
};

export interface IntentRequest {
  readonly intent: CommandIntent;
  readonly clientKey: string;
  readonly accountId: string;
  readonly environment: Environment;
  readonly mode: Mode;
  readonly reason: string;
  readonly expectedVersions: Readonly<Record<string, number>>;
  readonly expiresAt: string;
  /** The intent's own scope (an order id, an instrument and a quantity); merged under `intent`. */
  readonly scope?: Readonly<Record<string, string | number | { readonly text: string }>>;
}

/** Build the wire request of an intent; identity and rights are never part of it. */
export function buildRequest(request: IntentRequest): CommandRequest {
  return {
    clientKey: request.clientKey,
    action: INTENTS[request.intent].action,
    accountId: request.accountId,
    environment: request.environment,
    mode: request.mode,
    payload: { ...request.scope, intent: request.intent },
    reason: request.reason,
    targetVersions: request.expectedVersions,
    expiresAt: request.expiresAt,
  };
}

/** Disable or enable a command control from the shared flag (reconciliation, stale, unsupported). */
export function setCommandEnabled(control: HTMLElement, enabled: boolean): void {
  control.toggleAttribute("disabled", !enabled);
  control.setAttribute("aria-disabled", String(!enabled));
}

/** The accessible name of a control that opens an intent: action, account and scope (V08-A06). */
export function controlName(intent: CommandIntent, accountId: string, scope: string): string {
  return `${INTENTS[intent].label}: account ${accountId}, scope ${scope}`;
}

/** Why a reason is not acceptable, or `null`: non-blank and within the server's bound. */
export function reasonProblem(reason: string): string | null {
  if (reason.trim() === "") {
    return "Give a reason: it is recorded with the command.";
  }
  return reason.length > REASON_MAX
    ? `The reason is longer than ${String(REASON_MAX)} characters.`
    : null;
}

/** Why a quantity is not acceptable, or `null`: a whole number from 1 to `max`. */
export function quantityProblem(text: string, max: number): string | null {
  if (!/^[1-9]\d{0,9}$/.test(text.trim())) {
    return "Enter a whole quantity of at least 1.";
  }
  return Number(text.trim()) > max ? `At most ${String(max)} can be reduced.` : null;
}

// --- the progression (UI spec §4: requested -> accepted or rejected -> applying -> applied, failed or unknown)

export type Stage =
  | "requested"
  | "awaiting-approval"
  | "accepted"
  | "rejected"
  | "applying"
  | "applied"
  | "failed"
  | "unknown"
  | "expired"
  | "cancelled";

const STAGES: Readonly<Record<CommandState, Stage>> = {
  RECEIVED: "requested",
  PENDING_APPROVAL: "awaiting-approval",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  APPLYING: "applying",
  APPLIED: "applied",
  FAILED: "failed",
  UNKNOWN: "unknown",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
};

/** The stage a server state shows as; every state the server can answer has one. */
export function stageOf(state: CommandState): Stage {
  return STAGES[state];
}

/** The server states, for completeness checks. */
export const ALL_STAGES: readonly Stage[] = COMMAND_STATES.map((state) => STAGES[state]);

const SETTLED = new Set<CommandState>(["REJECTED", "APPLIED", "FAILED", "EXPIRED", "CANCELLED"]);

/** Whether polling can stop: the command reached an end the server will not move on from. */
export function isSettled(state: CommandState): boolean {
  return SETTLED.has(state);
}

/** Whether the server will still move the command (so the dialog keeps asking). */
export function isMoving(state: CommandState): boolean {
  return !isSettled(state) && state !== "UNKNOWN";
}

/** Whether a settled state frees the intent for a reviewed new request (a new identity). */
export function freesIdentity(state: CommandState): boolean {
  return state === "REJECTED" || state === "FAILED" || state === "EXPIRED" || state === "CANCELLED";
}

const BROKER_DONE = new Set(["FILLED", "CANCELLED", "REJECTED"]);

export interface BrokerNote {
  readonly orderId: string;
  readonly brokerState: string;
  readonly text: string;
  /** Whether the order still leaves exposure unresolved: partial, unknown or in flight. */
  readonly unresolved: boolean;
}

/** The broker or execution states of a command's orders, each stated for what it is (UX04). */
export function brokerNotes(outcome: CommandOutcome): readonly BrokerNote[] {
  return outcome.brokerStates.map(({ orderId, brokerState }) => {
    const unresolved = !BROKER_DONE.has(brokerState);
    const text =
      brokerState === "PARTIALLY_FILLED"
        ? "partially filled: not fully filled, the residual is still open"
        : brokerState === "UNKNOWN"
          ? "unknown: the broker has not confirmed it; do not assume it is filled or closed"
          : brokerState === "FILLED"
            ? "filled, as the broker reported"
            : unresolved
              ? "in flight at the broker; not a fill"
              : `${brokerState.toLowerCase()}, as the broker reported`;
    return { orderId, brokerState, text, unresolved };
  });
}

/** What the dialog says about a command's outcome beyond its stage; an HTTP 2xx is never a fill. */
export function outcomeNotice(outcome: CommandOutcome): string {
  if (outcome.state === "UNKNOWN") {
    return "The outcome is unknown. It stays open for inspection; re-check it, and do not assume the order was filled or the position closed.";
  }
  if (outcome.state === "APPLYING") {
    return "The controller is applying the command. This is not a fill.";
  }
  if (outcome.state === "APPLIED") {
    return "The controller applied the command. Any orders it created have their own broker lifecycle below; applied is not filled.";
  }
  if (outcome.state === "FAILED") {
    return "The command failed. Check the orders below before acting again: part of it may have been applied.";
  }
  return "";
}

/** A UTC instant string (`...ffffffZ`) from microseconds since the epoch. */
export function instantFromMicros(micros: bigint): string {
  const millis = Number(micros / 1000n);
  const base = new Date(millis).toISOString().slice(0, 19);
  return `${base}.${(micros % 1_000_000n).toString().padStart(6, "0")}Z`;
}

// --- the P0-E guards (INC-0807): cooldown, written review, out-of-band code, modification cap, amount

/** What the server's routing facts add to a dialog; absent fields mean the INC-0609 dialog unchanged. */
export interface RoutingGuard {
  /** The server's clock at the facts' snapshot: every instant below is compared against it. */
  readonly clock: ServerClock;
  /** A resume refuses until the server's clock passes `endsAt` (WC-07). */
  readonly cooldown?: { readonly endsAt: string };
  /** A resume carries a written review. */
  readonly reviewRequired?: boolean;
  /** The reader's live code: its redacted `channel`; refused once `expiresAt` passes. Implies a code is required. */
  readonly outOfBand?: { readonly channel: string; readonly expiresAt: string };
  /** A code is required but none is live yet: the dialog asks for one (RISK_INCREASING, ASSISTED or LIVE). */
  readonly outOfBandRequired?: boolean;
  /** The order's modifications so far against the broker's cap. */
  readonly modification?: { readonly used: number; readonly cap: number };
  /** The amount must exceed this many paise (a risk increase raises a limit). */
  readonly amountAbovePaise?: bigint;
}

const RUPEES = /^(\d{1,9})(?:\.(\d{1,2}))?$/;
const OOB_CODE = /^\d{6,8}$/;

/** Exact rupees text (at most two decimals) as integer paise, or `null`; never a float. */
export function parsePaise(text: string): bigint | null {
  const match = RUPEES.exec(text.trim());
  if (match === null) {
    return null;
  }
  const [, whole = "0", fraction = ""] = match;
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
}

/**
 * Integer paise as the JSON number the wire carries: exact or refused, never rounded (review F3).
 * JSON has no bigint; every whole number up to 2^53 - 1 is exact in a double.
 */
export function paiseOnWire(paise: bigint | null): number {
  if (paise === null || paise < 0n || paise > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError("paise outside the exact integer range of the wire");
  }
  return Number(paise);
}

/** Why an amount is not acceptable, or `null`: positive rupees, above the floor when there is one. */
export function amountProblem(text: string, above: bigint | undefined): string | null {
  const paise = parsePaise(text);
  if (paise === null || paise <= 0n) {
    return "Enter the amount in rupees, with at most two decimals, for example 1285.50.";
  }
  return above !== undefined && paise <= above
    ? "A risk increase must be above the current limit."
    : null;
}

/** Why a written review is missing, or `null`. */
export function reviewProblem(text: string): string | null {
  return text.trim() === "" ? "Write the review: a resume needs it." : null;
}

/** The time left of a cooldown in words, from whole seconds. */
export function cooldownText(seconds: number): string {
  return formatDurationUs(seconds * 1_000_000);
}

/** Why a resume must wait, or `null` once the cooldown has elapsed on the server's clock (WC-07). */
export function cooldownProblem(guard: RoutingGuard, mono: Mono): string | null {
  if (guard.cooldown === undefined) {
    return null;
  }
  const left = secondsLeft(guard.cooldown.endsAt, guard.clock, mono);
  return left === 0
    ? null
    : `The cooldown after the halt has not elapsed: ${cooldownText(left)} remain. Nothing was sent.`;
}

/** The intents whose action is RISK_INCREASING: only these ever need an out-of-band code. */
export const RISK_INCREASING_INTENTS: readonly CommandIntent[] = [
  "resume",
  "manual-takeover",
  "modify-order",
  "increase-risk",
];

/**
 * Whether a code is needed: a risk-increasing intent, and either the reader's live record says so or
 * the envelope's mode is ASSISTED or LIVE (an unknown mode fails closed). A record that says no code
 * never waives one the mode requires (Opus review F1: contradictory facts fail closed).
 */
export function codeRequired(
  intent: CommandIntent,
  facts: { readonly outOfBand: { readonly required: boolean } | null },
  mode: Mode | null,
): boolean {
  if (!RISK_INCREASING_INTENTS.includes(intent)) {
    return false;
  }
  return (
    facts.outOfBand?.required === true || mode === null || mode === "ASSISTED" || mode === "LIVE"
  );
}

/**
 * The code fields of a guard for an intent: the live record when there is one for this intent's
 * own action, else just "required". A code issued for another action is never shown as live here
 * (build-log item 168): the server would refuse it, so the dialog asks for a new one.
 */
export function codeGuardOf(
  intent: CommandIntent,
  facts: {
    readonly outOfBand: {
      readonly required: boolean;
      readonly action: string;
      readonly channel: string;
      readonly expiresAt: string;
    } | null;
  },
  mode: Mode | null,
): Pick<RoutingGuard, "outOfBand" | "outOfBandRequired"> {
  if (!codeRequired(intent, facts, mode)) {
    return {};
  }
  const live = facts.outOfBand;
  return live?.action === INTENTS[intent].action
    ? { outOfBand: { channel: live.channel, expiresAt: live.expiresAt } }
    : { outOfBandRequired: true };
}

/**
 * Why the out-of-band code cannot be sent, or `null`: typed by hand, in the shape, not expired.
 * `issued` is a code asked for from the dialog, which stands in for the guard's record.
 */
export function outOfBandProblem(
  code: string,
  guard: RoutingGuard,
  mono: Mono,
  issued?: { readonly channel: string; readonly expiresAt: string },
): string | null {
  const live = issued ?? guard.outOfBand;
  if (live === undefined) {
    return guard.outOfBandRequired === true
      ? "A confirmation code is needed: ask for one first. Nothing was sent."
      : null;
  }
  if (parseInstant(live.expiresAt) <= serverNowMicros(guard.clock, mono)) {
    return "The out-of-band confirmation has expired. Ask for a new one through the other channel.";
  }
  return OOB_CODE.test(code.trim())
    ? null
    : `Enter the confirmation code sent by ${live.channel} (6 to 8 digits).`;
}

/** Why a modification is refused, or `null`: the order has used the broker's cap. */
export function modificationProblem(guard: RoutingGuard): string | null {
  const found = guard.modification;
  return found !== undefined && found.used >= found.cap
    ? `This order has used all ${String(found.cap)} of its allowed modifications. Cancel it and enter a new order instead.`
    : null;
}
